import * as React from 'react';

import { addModelFileToDatabase } from 'src/actions/projects/action-creators/addModelFileToDatabase';
import { useAddImportedModels } from 'src/containers/ProjectModelDialog/hooks/useAddImportedModels';
import { useAddLocalModel } from 'src/containers/ProjectModelDialog/hooks/useAddLocalModel';
import {
  AddModelType,
  useAddModelType,
} from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import { useAddUploadModel } from 'src/containers/ProjectModelDialog/hooks/useAddUploadModel';
import { useCloseModelDialog } from 'src/containers/ProjectModelDialog/hooks/useCloseModelDialog';
import { useImportModel } from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import { useRequestModelData } from 'src/containers/ProjectModelDialog/hooks/useRequestModelData';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { checkImportModelLimits } from 'src/utils/checkImportModelLimits';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

export const useSubmitAddModel = () => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();
  const { addModelType } = useAddModelType();

  const { handleAddLocalModel, isAddLocalModelEnabled } = useAddLocalModel();
  const { handleAddImportedModel, isAddImportedModelsEnabled } =
    useAddImportedModels();
  const { isAddUploadModelEnabled } = useAddUploadModel();

  const { dataModels, pendingModelData, setPendingModelData } =
    useImportModel();
  const {
    databaseId: uploadDatabaseId,
    file,
    name: uploadModelName,
    namedGraphIri,
    pendingModelData: uploadPendingModelData,
    setPendingModelData: setUploadPendingModelData,
  } = useUploadModel();

  const handleRequestModelData = useRequestModelData(addModelType);
  const handleCloseDialog = useCloseModelDialog();

  return React.useCallback(async () => {
    if (addModelType === AddModelType.LOCAL && isAddLocalModelEnabled) {
      handleAddLocalModel();
      handleCloseDialog();
    } else if (
      addModelType === AddModelType.IMPORT &&
      isAddImportedModelsEnabled
    ) {
      // If we don't have pending model data yet, fetch it first
      if (!Object.keys(pendingModelData).length && dataModels.length) {
        try {
          const modelData = await handleRequestModelData(dataModels);

          if (!isMounted.current) {
            return;
          }

          setPendingModelData(modelData);

          // Check if the fetched model is within size limits
          const isOverLimit = checkImportModelLimits(modelData);

          // If model is within size limits, add it and close dialog
          if (!isOverLimit) {
            handleAddImportedModel(modelData);
            handleCloseDialog();
          }
          // If over limit, don't close dialog - the callout will show in ImportModelPanel
        } catch (error) {
          console.error('Error fetching model data:', error);
        }
      } else {
        handleAddImportedModel(pendingModelData);
        handleCloseDialog();
      }
    } else if (
      addModelType === AddModelType.UPLOAD &&
      isAddUploadModelEnabled &&
      file &&
      uploadDatabaseId
    ) {
      // Add model to database first
      if (!uploadPendingModelData) {
        try {
          const didAddFileSucceed = await dispatch(
            addModelFileToDatabase({
              databaseId: uploadDatabaseId,
              file,
              modelName: uploadModelName,
              namedGraphIri,
            })
          );

          if (!didAddFileSucceed) {
            return;
          }

          // fetch initial model data
          const modelData = await handleRequestModelData([uploadModelName]);

          if (!isMounted.current || !Object.keys(modelData).length) {
            return;
          }

          setUploadPendingModelData(Object.values(modelData)[0]);

          // Check if the fetched model is within size limits
          const isOverLimit = checkImportModelLimits(modelData);

          // If model is within size limits, add it and close dialog
          if (!isOverLimit) {
            handleAddImportedModel(modelData);
            handleCloseDialog();
          }
          // If over limit, don't close dialog - the callout will show in ImportModelPanel
        } catch (error) {
          console.error('Error fetching model data:', error);
        }
      } else {
        handleAddImportedModel({
          [uploadPendingModelData.modelName]: uploadPendingModelData,
        });
        handleCloseDialog();
      }
    }
  }, [
    addModelType,
    dataModels,
    dispatch,
    file,
    isAddLocalModelEnabled,
    isAddImportedModelsEnabled,
    isAddUploadModelEnabled,
    isMounted,
    handleAddLocalModel,
    handleCloseDialog,
    handleAddImportedModel,
    handleRequestModelData,
    namedGraphIri,
    pendingModelData,
    setPendingModelData,
    setUploadPendingModelData,
    uploadDatabaseId,
    uploadModelName,
    uploadPendingModelData,
  ]);
};
